import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { database, storage } from "../../firebaseConfig";
import { ref, set, push, onValue, get, update, remove } from "firebase/database";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import OfferServiceItem from "./OfferServiceItem.js";
import OfferServiceQuickRow from "./OfferServiceQuickRow";
import OfferPreviewPage from "./OfferPreviewPage";
import DropdownMenu from "./DropdownMenu";
import DocumentNumberField from "./DocumentNumberField";
import ReissueInvoiceModal from "./ReissueInvoiceModal";
import {
  analyzeDocumentNumber,
  buildNumberIndex,
  collectDocumentNumbers,
} from "./utils/documentNumbers";
import { calculateOfferPricing } from "./utils/offerPricing";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { PAYMENT_TYPES, getPaymentType, isRecurringFromPaymentType } from "./OfferLanguage";
import "./OfferMaker.css";

// Default payment term used to derive the invoice due date from its date.
const PAYMENT_TERM_DAYS = 7;

const toDateInputValue = (date) => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return '';
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ServiceSelectionModal Component
const ServiceSelectionModal = ({ 
  isOpen, 
  onClose, 
  services, 
  selectedServiceIds,
  onSelectService, 
  searchTerm, 
  setSearchTerm,
  categoryFilter,
  setCategoryFilter,
  categories
}) => {
  if (!isOpen) return null;

  return (
    <div className="offer-maker__modal-overlay">
      <div className="offer-maker__modal">
        <div className="offer-maker__modal-header">
          <h3 className="offer-maker__modal-title">
            <span className="offer-maker__modal-icon">🛠️</span>
            Add Services to Offer
          </h3>
          <button 
            className="offer-maker__modal-close" 
            onClick={onClose}
            title="Close"
          >
            ×
          </button>
        </div>
        
        <div className="offer-maker__modal-filters">
          <div className="offer-maker__search-container">
            <input 
              type="text" 
              placeholder="Search services..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="offer-maker__search-input"
            />
            {searchTerm && (
              <button 
                className="offer-maker__clear-search" 
                onClick={() => setSearchTerm("")}
              >
                ×
              </button>
            )}
          </div>
          
          <div className="offer-maker__category-filter">
            <select 
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="offer-maker__category-select"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="offer-maker__modal-content">
          {services.length === 0 ? (
            <div className="offer-maker__empty-state">
              <p>No services found matching your filter criteria.</p>
            </div>
          ) : (
            <div className="offer-maker__services-grid">
              {services.map(service => (
                <div 
                  key={service.id} 
                  className={`offer-maker__service-selection-item ${
                    selectedServiceIds.includes(service.id) ? 'offer-maker__service-selection-item--selected' : ''
                  }`}
                  onClick={() => onSelectService(service.id)}
                >
                  <OfferServiceItem 
                    service={service} 
                    isCompact={true} 
                    onEdit={() => {}} 
                    onDelete={() => {}}
                    onDuplicate={() => {}}
                  />
                  <div className="offer-maker__service-selection-checkbox">
                    {selectedServiceIds.includes(service.id) ? (
                      <span className="offer-maker__checkbox-checked">✓</span>
                    ) : (
                      <span className="offer-maker__checkbox-unchecked"></span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="offer-maker__modal-footer">
          <button 
            className="offer-maker__modal-cancel" 
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="offer-maker__modal-save" 
            onClick={onClose}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

// MicroserviceEditorModal Component - Edit microservices for a specific service in this offer only
const MicroserviceEditorModal = ({
  isOpen,
  onClose,
  service,
  allMicroservices,
  overrides,
  serviceOverride: initialServiceOverride,
  additionalMicroservices: initialAdditionalMs,
  removedMicroservices: initialRemovedMs,
  onSaveOverrides
}) => {
  const [localOverrides, setLocalOverrides] = useState({});
  const [additionalMicroservices, setAdditionalMicroservices] = useState([]); // IDs of added microservices
  const [removedMicroservices, setRemovedMicroservices] = useState([]); // IDs of removed original microservices
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  // Service-level overrides (name, price, recurring)
  const [serviceTitle, setServiceTitle] = useState("");
  const [serviceDescription, setServiceDescription] = useState("");
  const [servicePrice, setServicePrice] = useState("");
  const [servicePaymentType, setServicePaymentType] = useState(null); // null = use original

  // Initialize local overrides and additional microservices when modal opens
  useEffect(() => {
    if (isOpen && service) {
      setLocalOverrides(overrides || {});
      setAdditionalMicroservices(initialAdditionalMs || []);
      setRemovedMicroservices(initialRemovedMs || []);
      setShowAddPanel(false);
      setSearchTerm("");
      // Initialize service-level overrides
      setServiceTitle(initialServiceOverride?.title || "");
      setServiceDescription(initialServiceOverride?.description || "");
      setServicePrice(initialServiceOverride?.price !== undefined ? initialServiceOverride.price.toString() : "");
      // Read paymentType override; fall back to legacy isRecurring boolean override
      if (initialServiceOverride?.paymentType !== undefined) {
        setServicePaymentType(initialServiceOverride.paymentType);
      } else if (initialServiceOverride?.isRecurring !== undefined) {
        setServicePaymentType(
          initialServiceOverride.isRecurring ? PAYMENT_TYPES.RECURRING : PAYMENT_TYPES.ONE_TIME
        );
      } else {
        setServicePaymentType(null);
      }
    }
  }, [isOpen, service, overrides, initialAdditionalMs, initialRemovedMs, initialServiceOverride]);

  if (!isOpen || !service) return null;

  // Get microservices for this service
  // Handle both formats: array of IDs or array of objects
  const rawMicroservices = service.microservices || [];
  
  // Check if it's an array of objects or an array of IDs
  const baseMicroservices = rawMicroservices.length > 0 && typeof rawMicroservices[0] === 'object'
    ? rawMicroservices // Already objects with full data
    : allMicroservices.filter(ms => rawMicroservices.includes(ms.id)); // IDs - need to look up

  // Filter out removed microservices from base
  const filteredBaseMicroservices = baseMicroservices.filter(ms => !removedMicroservices.includes(ms.id));

  // Get additional microservices (added for this offer only)
  const addedMicroservices = allMicroservices.filter(ms => additionalMicroservices.includes(ms.id));
  
  // Combine base (minus removed) + additional
  const serviceMicroservices = [...filteredBaseMicroservices, ...addedMicroservices];
  
  // Check if a microservice is an original (part of the service's base microservices)
  const isOriginalMicroservice = (msId) => {
    return baseMicroservices.some(ms => ms.id === msId);
  };
  
  // Check if a microservice has been removed
  const isRemovedMicroservice = (msId) => {
    return removedMicroservices.includes(msId);
  };

  // Get microservices that can be added (not already in service)
  const existingIds = serviceMicroservices.map(ms => ms.id);
  const availableToAdd = allMicroservices.filter(ms => 
    !existingIds.includes(ms.id) &&
    (searchTerm === "" || ms.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
     (ms.category && ms.category.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  // Get effective value (override or original)
  const getEffectiveValue = (msId, field) => {
    if (localOverrides[msId] && localOverrides[msId][field] !== undefined) {
      return localOverrides[msId][field];
    }
    const ms = allMicroservices.find(m => m.id === msId);
    return ms ? ms[field] : '';
  };

  // Check if a field has been overridden
  const isOverridden = (msId, field) => {
    return localOverrides[msId] && localOverrides[msId][field] !== undefined;
  };

  // Update an override
  const handleOverrideChange = (msId, field, value) => {
    setLocalOverrides(prev => ({
      ...prev,
      [msId]: {
        ...prev[msId],
        [field]: value
      }
    }));
  };

  // Reset a specific field to original
  const resetField = (msId, field) => {
    setLocalOverrides(prev => {
      const newOverrides = { ...prev };
      if (newOverrides[msId]) {
        delete newOverrides[msId][field];
        // If no more overrides for this microservice, remove the key
        if (Object.keys(newOverrides[msId]).length === 0) {
          delete newOverrides[msId];
        }
      }
      return newOverrides;
    });
  };

  // Reset all overrides for this service
  const resetAllOverrides = () => {
    setLocalOverrides({});
    setAdditionalMicroservices([]);
    setRemovedMicroservices([]);
    setServiceTitle("");
    setServiceDescription("");
    setServicePrice("");
    setServicePaymentType(null);
  };
  
  // Remove an original microservice from this offer
  const handleRemoveOriginalMicroservice = (msId) => {
    setRemovedMicroservices(prev => [...prev, msId]);
    // Also remove any overrides for this microservice
    setLocalOverrides(prev => {
      const newOverrides = { ...prev };
      delete newOverrides[msId];
      return newOverrides;
    });
  };
  
  // Restore a removed microservice
  const handleRestoreMicroservice = (msId) => {
    setRemovedMicroservices(prev => prev.filter(id => id !== msId));
  };

  // Check if service fields are overridden
  const isServiceTitleOverridden = serviceTitle !== "" && serviceTitle !== service.title;
  const isServiceDescriptionOverridden = serviceDescription !== "" && serviceDescription !== (service.description || "");
  const isServicePriceOverridden = servicePrice !== "" && parseFloat(servicePrice) !== service.price;
  const originalPaymentType = getPaymentType(service);
  const isServicePaymentTypeOverridden =
    servicePaymentType !== null && servicePaymentType !== originalPaymentType;

  // Effective payment type (override or original)
  const effectivePaymentType =
    servicePaymentType !== null ? servicePaymentType : originalPaymentType;

  // Add a microservice to this offer
  const handleAddMicroservice = (msId) => {
    setAdditionalMicroservices(prev => [...prev, msId]);
  };

  // Remove an added microservice
  const handleRemoveAddedMicroservice = (msId) => {
    setAdditionalMicroservices(prev => prev.filter(id => id !== msId));
    // Also remove any overrides for this microservice
    setLocalOverrides(prev => {
      const newOverrides = { ...prev };
      delete newOverrides[msId];
      return newOverrides;
    });
  };

  // Check if microservice was added (not part of original service)
  const isAddedMicroservice = (msId) => {
    return additionalMicroservices.includes(msId);
  };

  // Save and close
  const handleSave = () => {
    // Build service override object
    const serviceOverrideData = {};
    if (serviceTitle && serviceTitle !== service.title) {
      serviceOverrideData.title = serviceTitle;
    }
    if (serviceDescription && serviceDescription !== (service.description || "")) {
      serviceOverrideData.description = serviceDescription;
    }
    if (servicePrice && parseFloat(servicePrice) !== service.price) {
      serviceOverrideData.price = parseFloat(servicePrice);
    }
    if (servicePaymentType !== null && servicePaymentType !== originalPaymentType) {
      serviceOverrideData.paymentType = servicePaymentType;
      // Keep legacy field in sync for backwards compatibility
      serviceOverrideData.isRecurring = isRecurringFromPaymentType(servicePaymentType);
    }
    
    onSaveOverrides(service.id, localOverrides, additionalMicroservices, serviceOverrideData, removedMicroservices);
    onClose();
  };

  const hasAnyChanges = Object.keys(localOverrides).length > 0 || 
                        additionalMicroservices.length > 0 || 
                        removedMicroservices.length > 0 ||
                        isServiceTitleOverridden || 
                        isServiceDescriptionOverridden ||
                        isServicePriceOverridden ||
                        isServicePaymentTypeOverridden;

  return (
    <div className="offer-maker__modal-overlay">
      <div className="offer-maker__modal offer-maker__modal--microservices">
        <div className="offer-maker__modal-header">
          <h3 className="offer-maker__modal-title">
            <span className="offer-maker__modal-icon">⚙️</span>
            Customize Microservices for this Offer
          </h3>
          <button 
            className="offer-maker__modal-close" 
            onClick={onClose}
            title="Close"
          >
            ×
          </button>
        </div>

        <div className="offer-maker__microservice-editor-info">
          <div className="offer-maker__microservice-editor-service-name">
            📦 Service: <strong>{service.title}</strong>
          </div>
          <p className="offer-maker__microservice-editor-hint">
            Changes made here only apply to this offer. The original microservices remain unchanged.
          </p>
          <div className="offer-maker__microservice-editor-actions">
            <button 
              className={`offer-maker__add-microservice-btn ${showAddPanel ? 'offer-maker__add-microservice-btn--active' : ''}`}
              onClick={() => setShowAddPanel(!showAddPanel)}
            >
              {showAddPanel ? '✕ Close' : '➕ Add Microservice'}
            </button>
            {hasAnyChanges && (
              <button 
                className="offer-maker__reset-all-btn"
                onClick={resetAllOverrides}
              >
                🔄 Reset all
              </button>
            )}
          </div>
        </div>

        {/* Scrollable body wrapper */}
        <div className="offer-maker__modal-body-scroll">
          {/* Service Override Section */}
          <div className="offer-maker__service-override-section">
            <div className="offer-maker__service-override-header">
              <span className="offer-maker__service-override-icon">📝</span>
              <span>Service Details (for this offer only)</span>
            </div>
          <div className="offer-maker__service-override-fields">
            <div className="offer-maker__service-override-field">
              <label className="offer-maker__service-override-label">
                Service Name
                {isServiceTitleOverridden && (
                  <button 
                    className="offer-maker__field-reset-btn"
                    onClick={() => setServiceTitle("")}
                    title="Reset to original"
                  >
                    ↩
                  </button>
                )}
              </label>
              <input
                type="text"
                className={`offer-maker__service-override-input ${isServiceTitleOverridden ? 'offer-maker__service-override-input--modified' : ''}`}
                value={serviceTitle || service.title}
                onChange={(e) => setServiceTitle(e.target.value)}
                placeholder={service.title}
              />
              <span className="offer-maker__service-override-original">Original: {service.title}</span>
            </div>
            <div className="offer-maker__service-override-field offer-maker__service-override-field--full">
              <label className="offer-maker__service-override-label">
                Description
                {isServiceDescriptionOverridden && (
                  <button 
                    className="offer-maker__field-reset-btn"
                    onClick={() => setServiceDescription("")}
                    title="Reset to original"
                  >
                    ↩
                  </button>
                )}
              </label>
              <textarea
                className={`offer-maker__service-override-textarea ${isServiceDescriptionOverridden ? 'offer-maker__service-override-input--modified' : ''}`}
                value={serviceDescription || service.description || ''}
                onChange={(e) => setServiceDescription(e.target.value)}
                placeholder={service.description || 'Add a description...'}
                rows="3"
              />
              {service.description && (
                <span className="offer-maker__service-override-original">Original: {service.description?.substring(0, 100)}{service.description?.length > 100 ? '...' : ''}</span>
              )}
            </div>
            <div className="offer-maker__service-override-field">
              <label className="offer-maker__service-override-label">
                Total Price (€)
                {isServicePriceOverridden && (
                  <button 
                    className="offer-maker__field-reset-btn"
                    onClick={() => setServicePrice("")}
                    title="Reset to original"
                  >
                    ↩
                  </button>
                )}
              </label>
              <input
                type="number"
                className={`offer-maker__service-override-input ${isServicePriceOverridden ? 'offer-maker__service-override-input--modified' : ''}`}
                value={servicePrice || service.price}
                onChange={(e) => setServicePrice(e.target.value)}
                placeholder={service.price?.toString()}
                step="0.01"
                min="0"
              />
              <span className="offer-maker__service-override-original">Original: €{service.price?.toFixed(2) || '0.00'}</span>
            </div>
            <div className="offer-maker__service-override-field offer-maker__service-override-field--billing">
              <label className="offer-maker__service-override-label">
                Billing Type
                {isServicePaymentTypeOverridden && (
                  <button
                    className="offer-maker__field-reset-btn"
                    onClick={() => setServicePaymentType(null)}
                    title="Reset to original"
                  >
                    ↩
                  </button>
                )}
              </label>
              <div className={`offer-maker__billing-type-toggle ${isServicePaymentTypeOverridden ? 'offer-maker__billing-type-toggle--modified' : ''}`}>
                <button
                  type="button"
                  className={`offer-maker__billing-type-btn ${effectivePaymentType === PAYMENT_TYPES.ONE_TIME ? 'offer-maker__billing-type-btn--active' : ''}`}
                  onClick={() => setServicePaymentType(PAYMENT_TYPES.ONE_TIME)}
                >
                  💰 One Time
                </button>
                <button
                  type="button"
                  className={`offer-maker__billing-type-btn ${effectivePaymentType === PAYMENT_TYPES.PER_APPOINTMENT ? 'offer-maker__billing-type-btn--active' : ''}`}
                  onClick={() => setServicePaymentType(PAYMENT_TYPES.PER_APPOINTMENT)}
                >
                  📅 Per Appointment
                </button>
                <button
                  type="button"
                  className={`offer-maker__billing-type-btn ${effectivePaymentType === PAYMENT_TYPES.RECURRING ? 'offer-maker__billing-type-btn--active' : ''}`}
                  onClick={() => setServicePaymentType(PAYMENT_TYPES.RECURRING)}
                >
                  🔄 Recurring
                </button>
              </div>
              <span className="offer-maker__service-override-original">
                Original: {(() => {
                  if (originalPaymentType === PAYMENT_TYPES.RECURRING) return '🔄 Recurring';
                  if (originalPaymentType === PAYMENT_TYPES.PER_APPOINTMENT) return '📅 Per Appointment';
                  return '💰 One Time';
                })()}
              </span>
            </div>
          </div>
        </div>

        {/* Add Microservice Panel */}
        {showAddPanel && (
          <div className="offer-maker__add-microservice-panel">
            <input
              type="text"
              className="offer-maker__add-microservice-search"
              placeholder="Search microservices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="offer-maker__add-microservice-list">
              {availableToAdd.length === 0 ? (
                <p className="offer-maker__add-microservice-empty">
                  {searchTerm ? 'No matching microservices found' : 'All microservices already added'}
                </p>
              ) : (
                availableToAdd.slice(0, 10).map(ms => (
                  <div 
                    key={ms.id} 
                    className="offer-maker__add-microservice-item"
                    onClick={() => handleAddMicroservice(ms.id)}
                  >
                    <span className="offer-maker__add-microservice-item-title">{ms.title}</span>
                    {ms.category && (
                      <span className="offer-maker__add-microservice-item-category">{ms.category}</span>
                    )}
                    <span className="offer-maker__add-microservice-item-add">+</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        
        <div className="offer-maker__modal-content offer-maker__microservice-editor-content">
          {/* Show removed microservices that can be restored */}
          {removedMicroservices.length > 0 && (
            <div className="offer-maker__removed-microservices-section">
              <div className="offer-maker__removed-microservices-header">
                <span>🗑️ Removed from this offer ({removedMicroservices.length})</span>
              </div>
              <div className="offer-maker__removed-microservices-list">
                {baseMicroservices
                  .filter(ms => removedMicroservices.includes(ms.id))
                  .map(ms => (
                    <div key={ms.id} className="offer-maker__removed-microservice-item">
                      <span className="offer-maker__removed-microservice-title">{ms.title}</span>
                      <button
                        className="offer-maker__restore-microservice-btn"
                        onClick={() => handleRestoreMicroservice(ms.id)}
                        title="Restore this microservice"
                      >
                        ↩ Restore
                      </button>
                    </div>
                  ))
                }
              </div>
            </div>
          )}
          
          {serviceMicroservices.length === 0 ? (
            <div className="offer-maker__empty-state">
              <p>No microservices yet. Click "Add Microservice" to add some.</p>
            </div>
          ) : (
            <div className="offer-maker__microservice-editor-list">
              {serviceMicroservices.map(ms => (
                <div key={ms.id} className={`offer-maker__microservice-editor-item ${isAddedMicroservice(ms.id) ? 'offer-maker__microservice-editor-item--added' : ''}`}>
                  <div className="offer-maker__microservice-editor-item-header">
                    <span className="offer-maker__microservice-original-title">
                      {isAddedMicroservice(ms.id) && <span className="offer-maker__added-badge">➕ Added</span>}
                      {ms.title}
                    </span>
                    <div className="offer-maker__microservice-header-actions">
                      {ms.category && (
                        <span className="offer-maker__microservice-category-badge">
                          {ms.category}
                        </span>
                      )}
                      <button 
                        className="offer-maker__remove-microservice-btn"
                        onClick={() => isAddedMicroservice(ms.id) 
                          ? handleRemoveAddedMicroservice(ms.id) 
                          : handleRemoveOriginalMicroservice(ms.id)
                        }
                        title={isAddedMicroservice(ms.id) ? "Remove added microservice" : "Hide from this offer"}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  
                  <div className="offer-maker__microservice-editor-fields">
                    {/* Title Override */}
                    <div className="offer-maker__microservice-field">
                      <label className="offer-maker__microservice-field-label">
                        Title
                        {isOverridden(ms.id, 'title') && (
                          <button 
                            className="offer-maker__field-reset-btn"
                            onClick={() => resetField(ms.id, 'title')}
                            title="Reset to original"
                          >
                            ↩
                          </button>
                        )}
                      </label>
                      <input
                        type="text"
                        className={`offer-maker__microservice-field-input ${isOverridden(ms.id, 'title') ? 'offer-maker__microservice-field-input--modified' : ''}`}
                        value={getEffectiveValue(ms.id, 'title')}
                        onChange={(e) => handleOverrideChange(ms.id, 'title', e.target.value)}
                        placeholder={ms.title}
                      />
                    </div>

                    {/* Description Override */}
                    <div className="offer-maker__microservice-field offer-maker__microservice-field--full">
                      <label className="offer-maker__microservice-field-label">
                        Description
                        {isOverridden(ms.id, 'description') && (
                          <button 
                            className="offer-maker__field-reset-btn"
                            onClick={() => resetField(ms.id, 'description')}
                            title="Reset to original"
                          >
                            ↩
                          </button>
                        )}
                      </label>
                      <textarea
                        className={`offer-maker__microservice-field-textarea ${isOverridden(ms.id, 'description') ? 'offer-maker__microservice-field-input--modified' : ''}`}
                        value={getEffectiveValue(ms.id, 'description')}
                        onChange={(e) => handleOverrideChange(ms.id, 'description', e.target.value)}
                        placeholder={ms.description || 'No description'}
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        </div> {/* End of scrollable body wrapper */}
        
        <div className="offer-maker__modal-footer">
          <button 
            className="offer-maker__modal-cancel" 
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="offer-maker__modal-save" 
            onClick={handleSave}
          >
            💾 Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Component OfferMaker
const OfferMaker = ({ userId, initialOfferData = null, isEditMode = false, onOfferSaved = () => {}, onBack = () => {} }) => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [microservices, setMicroservices] = useState([]); // All microservices from DB
  const [currentOffer, setCurrentOffer] = useState({
    title: "",
    description: "",
    services: [],
    microserviceOverrides: {}, // Store microservice overrides per service
    additionalMicroservices: {}, // Store additional microservices per service (only for this offer)
    removedMicroservices: {}, // Store removed microservices per service (only for this offer)
    serviceOverrides: {}, // Store service-level overrides (title, price) per service
    notes: "",
    terms: "",
    showSignatureSection: false,
    signatureCustomerName: "",
    signatureCustomerRole: "",
    signaturePlace: "",
    logo: "",
    logoMaxHeight: 80, // Standard-Höhe
    createdAt: Date.now(),
    offerValidTill: "",
    estimatedTimespan: "",
    isRecurring: false,
    customerName: "",
    customerAddress: "",
    globalDiscount: 0,
    showPaymentButton: false,
    paymentButtonText: "Pay with Stripe",
    paymentButtonLink: "",
    showSenderName: true,
    teamMember: null,
    language: "en",
    includeTax: false,
    taxRate: 20,
    reverseCharge: false,
    documentType: "offer", // "offer" or "invoice"
    invoiceNumber: "",
    invoiceDate: "",
    showOfferNumber: false,
    offerNumber: ""
  });
  const [offerId, setOfferId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categories, setCategories] = useState([]);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false); // State for the service selection modal
  const [isMicroserviceEditorOpen, setIsMicroserviceEditorOpen] = useState(false); // NEW: Microservice editor modal
  const [editingServiceForMicroservices, setEditingServiceForMicroservices] = useState(null); // NEW: Which service's microservices we're editing
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [autoSaveId, setAutoSaveId] = useState(null);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [lastSavedOffer, setLastSavedOffer] = useState(null);
  const [hasDraftRecovery, setHasDraftRecovery] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isReissueOpen, setIsReissueOpen] = useState(false);
  const [draftData, setDraftData] = useState(null);
  // All saved offers/invoices, used to detect duplicate document numbers.
  const [savedDocuments, setSavedDocuments] = useState({});
  const autoNumberedRef = useRef(false);
  // Last payment due date this component derived itself, so manual edits win.
  const autoPaymentDueRef = useRef(null);
  const paymentDuePrefilledRef = useRef(false);

  // Load services from the database
  useEffect(() => {
    if (!userId) return;

    const servicesRef = ref(database, `/World/${userId}/services`);
    
    const unsubscribe = onValue(servicesRef, (snapshot) => {
      if (snapshot.exists()) {
        const servicesData = snapshot.val();
        const servicesArray = Object.entries(servicesData).map(([id, data]) => ({
          id,
          ...data
        }));
        setServices(servicesArray);
        
        // Extract categories
        const uniqueCategories = [...new Set(servicesArray
          .map(service => service.category)
          .filter(category => category && category.trim() !== "")
        )];
        setCategories(uniqueCategories);
      } else {
        setServices([]);
        setCategories([]);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  // Load microservices from the database
  useEffect(() => {
    if (!userId) return;

    const microservicesRef = ref(database, `/World/${userId}/microservices`);
    
    const unsubscribe = onValue(microservicesRef, (snapshot) => {
      if (snapshot.exists()) {
        const microservicesData = snapshot.val();
        const microservicesArray = Object.entries(microservicesData).map(([id, data]) => ({
          id,
          ...data
        }));
        setMicroservices(microservicesArray);
      } else {
        setMicroservices([]);
      }
    });

    return () => unsubscribe();
  }, [userId]);

  // Keep every saved document number in memory so the number field can react
  // while typing instead of only failing on save.
  useEffect(() => {
    if (!userId) return;

    const offersRef = ref(database, `/World/${userId}/offers`);
    const unsubscribe = onValue(offersRef, (snapshot) => {
      setSavedDocuments(snapshot.exists() ? snapshot.val() : {});
    });

    return () => unsubscribe();
  }, [userId]);

  const invoiceNumberIndex = useMemo(
    () => buildNumberIndex(collectDocumentNumbers(savedDocuments, "invoice")),
    [savedDocuments]
  );

  const offerNumberIndex = useMemo(
    () => buildNumberIndex(collectDocumentNumbers(savedDocuments, "offer")),
    [savedDocuments]
  );

  const invoiceNumberAnalysis = useMemo(
    () =>
      analyzeDocumentNumber(currentOffer.invoiceNumber, invoiceNumberIndex, {
        currentId: offerId,
        noun: "invoice number",
      }),
    [currentOffer.invoiceNumber, invoiceNumberIndex, offerId]
  );

  const offerNumberAnalysis = useMemo(
    () =>
      analyzeDocumentNumber(currentOffer.offerNumber, offerNumberIndex, {
        currentId: offerId,
        noun: "offer number",
      }),
    [currentOffer.offerNumber, offerNumberIndex, offerId]
  );

  // Prefill the next invoice number once, so a fresh invoice starts gapless
  // without the user having to look up the last one.
  useEffect(() => {
    if (autoNumberedRef.current) return;
    if (currentOffer.documentType !== "invoice") return;
    if (String(currentOffer.invoiceNumber || "").trim()) return;

    const suggestion = invoiceNumberAnalysis.suggestion;
    if (!suggestion) return;

    autoNumberedRef.current = true;
    setCurrentOffer((prev) =>
      String(prev.invoiceNumber || "").trim()
        ? prev
        : { ...prev, invoiceNumber: suggestion }
    );
  }, [
    currentOffer.documentType,
    currentOffer.invoiceNumber,
    invoiceNumberAnalysis.suggestion,
  ]);

  // An invoice without a payment due date gets the default term once, so the
  // field is never empty just because the creation date was left untouched.
  useEffect(() => {
    if (paymentDuePrefilledRef.current) return;
    if (currentOffer.documentType !== "invoice") return;
    if (currentOffer.offerValidTill) return;
    if (!currentOffer.createdAt) return;

    const created = new Date(currentOffer.createdAt);
    if (Number.isNaN(created.getTime())) return;

    const due = toDateInputValue(
      new Date(
        created.getFullYear(),
        created.getMonth(),
        created.getDate() + PAYMENT_TERM_DAYS
      )
    );
    paymentDuePrefilledRef.current = true;
    autoPaymentDueRef.current = due;
    setCurrentOffer((prev) =>
      prev.offerValidTill ? prev : { ...prev, offerValidTill: due }
    );
  }, [
    currentOffer.documentType,
    currentOffer.offerValidTill,
    currentOffer.createdAt,
  ]);

  // Draft Recovery - Prüfe beim Mount ob ein Draft existiert
  // Nur für NEUE Offers, nicht für Edit-Mode
  useEffect(() => {
    const checkForDraft = async () => {
      if (!userId) return;
      
      // Im Edit-Mode niemals Draft-Modal anzeigen
      // Prüfe sowohl Props als auch ob bereits eine ID gesetzt ist
      if (isEditMode || initialOfferData || initialOfferData?.id) {
        console.log("🚫 Draft check skipped - Edit mode or has initialData");
        // Falls Draft-Modal offen war, schließen
        if (showDraftModal) {
          setShowDraftModal(false);
        }
        return;
      }
      
      // Prüfe localStorage für schnellen Zugriff
      const localDraftKey = `offer_draft_${userId}`;
      const localDraft = localStorage.getItem(localDraftKey);
      
      if (localDraft) {
        try {
          const parsedDraft = JSON.parse(localDraft);
          const draftAge = Date.now() - (parsedDraft.savedAt || 0);
          
          // Nur wenn Draft weniger als 7 Tage alt ist
          if (draftAge < 7 * 24 * 60 * 60 * 1000) {
            setDraftData(parsedDraft);
            setShowDraftModal(true);
            setHasDraftRecovery(true);
          } else {
            // Lösche alten Draft
            localStorage.removeItem(localDraftKey);
          }
        } catch (error) {
          console.error("Error parsing draft:", error);
          localStorage.removeItem(localDraftKey);
        }
      }
    };
    
    checkForDraft();
  }, [userId, isEditMode, initialOfferData]);

  // Initialize the offer with provided data, if available
  useEffect(() => {
    console.log("🔄 OfferMaker useEffect triggered:", { 
      initialOfferData, 
      isEditMode, 
      showDraftModal,
      hasData: !!initialOfferData,
      title: initialOfferData?.title 
    });
    
    // Wenn wir echte Daten haben (Edit-Mode), Draft-Modal schließen und Daten laden
    if (initialOfferData && showDraftModal) {
      console.log("🔄 Closing draft modal because we have real data");
      setShowDraftModal(false);
    }
    
    // Wenn wir gerade Draft Recovery anzeigen UND keine echten Daten haben, warte
    if (showDraftModal && !initialOfferData) {
      console.log("⏸️ Blocked by showDraftModal (no real data yet)");
      return;
    }
    
    if (initialOfferData) {
      console.log("📝 Setting currentOffer with:", initialOfferData.title);
      // Setze offerId wenn im Edit-Mode oder wenn eine ID vorhanden ist
      if (initialOfferData.id) {
        setOfferId(initialOfferData.id);
      } else {
        setOfferId(null);
      }
      
      // Stelle sicher, dass die Logo-Höhe nicht über 400px liegt
      const logoHeight = initialOfferData.logoMaxHeight ? 
        Math.min(400, Math.round(initialOfferData.logoMaxHeight / 5) * 5) : 
        80;

      setCurrentOffer({
        title: initialOfferData.title || "",
        description: initialOfferData.description || "",
        services: initialOfferData.services || [],
        microserviceOverrides: initialOfferData.microserviceOverrides || {},
        additionalMicroservices: initialOfferData.additionalMicroservices || {},
        removedMicroservices: initialOfferData.removedMicroservices || {}, // Load removed microservices!
        serviceOverrides: initialOfferData.serviceOverrides || {},
        notes: initialOfferData.notes || "",
        terms: initialOfferData.terms || "",
        showSignatureSection: initialOfferData.showSignatureSection || false,
        signatureCustomerName: initialOfferData.signatureCustomerName || "",
        signatureCustomerRole: initialOfferData.signatureCustomerRole || "",
        signaturePlace: initialOfferData.signaturePlace || "",
        logo: initialOfferData.logo || "",
        logoMaxHeight: logoHeight,
        createdAt: initialOfferData.createdAt || Date.now(),
        offerValidTill: initialOfferData.offerValidTill || "",
        estimatedTimespan: initialOfferData.estimatedTimespan || "",
        isRecurring: initialOfferData.isRecurring || false,
        customerName: initialOfferData.customerName || "",
        customerAddress: initialOfferData.customerAddress || "",
        globalDiscount: initialOfferData.globalDiscount || 0,
        showPaymentButton: initialOfferData.showPaymentButton || false,
        paymentButtonText: initialOfferData.paymentButtonText || "Pay with Stripe",
        paymentButtonLink: initialOfferData.paymentButtonLink || "",
        showSenderName: initialOfferData.showSenderName !== undefined ? initialOfferData.showSenderName : true,
        teamMember: initialOfferData.teamMember || null,
        language: initialOfferData.language || "en",
        includeTax: initialOfferData.includeTax || false,
        taxRate: initialOfferData.taxRate || 20,
        reverseCharge: initialOfferData.reverseCharge || false,
        documentType: initialOfferData.documentType || "offer",
        invoiceNumber: initialOfferData.invoiceNumber || "",
        invoiceDate: initialOfferData.invoiceDate || "",
        showOfferNumber: initialOfferData.showOfferNumber || false,
        offerNumber: initialOfferData.offerNumber || ""
      });
      
      if (initialOfferData.logo) {
        setLogoUrl(initialOfferData.logo);
      }

      if (initialOfferData.services && initialOfferData.services.length === 0) {
        setTimeout(() => {
          setIsModalOpen(true);
        }, 500);
      }
    }
  }, [initialOfferData, isEditMode, showDraftModal]);

  // Load team members
  useEffect(() => {
    const loadTeamMembers = async () => {
      if (!userId) return;

      try {
        const teamRef = ref(database, `/World/${userId}/team/members`);
        const snapshot = await get(teamRef);

        if (snapshot.exists()) {
          const members = [];
          snapshot.forEach((childSnapshot) => {
            members.push({
              id: childSnapshot.key,
              ...childSnapshot.val()
            });
          });
          setTeamMembers(members);

          // If editing and there's a team member ID, select it
          if (isEditMode && initialOfferData?.teamMemberId) {
            const member = members.find(m => m.id === initialOfferData.teamMemberId);
            if (member) {
              setSelectedTeamMember(member);
            }
          }
        }
      } catch (error) {
        console.error("Error loading team members:", error);
      }
    };

    loadTeamMembers();
  }, [userId, isEditMode, initialOfferData]);

  // Auto-Save Funktion - Speichert automatisch
  const autoSaveOffer = async (offerToSave) => {
    if (!userId) return;
    
    // Mindestanforderung: Es muss einen Titel geben
    if (!offerToSave.title || offerToSave.title.trim() === '') return;
    
    // Prüfe, ob tatsächlich Änderungen vorliegen
    if (lastSavedOffer && 
        JSON.stringify(offerToSave) === JSON.stringify(lastSavedOffer)) {
      return; // Keine Änderungen, kein Speichern nötig
    }
    
    try {
      // Berechne die Preise (nur wenn Services vorhanden sind)
      const { originalTotal, servicesDiscountedTotal, discountedTotal, taxAmount, finalTotal } =
        calculateOfferPricing(
          offerToSave,
          services.filter(service => (offerToSave.services || []).includes(service.id))
        );

      // Erstelle das komplette Offer-Objekt
      const updatedOffer = {
        ...offerToSave,
        originalTotal,
        servicesDiscountedTotal,
        discountedTotal,
        taxAmount,
        finalTotal,
        teamMemberId: selectedTeamMember?.id || null,
        teamMember: selectedTeamMember ? {
          name: selectedTeamMember.name,
          role: selectedTeamMember.role || "",
          companyName: selectedTeamMember.companyName,
          companySlogan: selectedTeamMember.companySlogan,
          personalEmail: selectedTeamMember.personalEmail,
          generalEmail: selectedTeamMember.generalEmail,
          phone: selectedTeamMember.phone,
          website: selectedTeamMember.website,
          billingStreet: selectedTeamMember.billingStreet || "",
          billingZip: selectedTeamMember.billingZip || "",
          billingCity: selectedTeamMember.billingCity || "",
          billingCountry: selectedTeamMember.billingCountry || "",
          vatId: selectedTeamMember.vatId || "",
          taxNumber: selectedTeamMember.taxNumber || "",
          tradeRegister: selectedTeamMember.tradeRegister || "",
          ceo: selectedTeamMember.ceo || "",
          bankName: selectedTeamMember.bankName,
          iban: selectedTeamMember.iban,
          bic: selectedTeamMember.bic,
          signature: selectedTeamMember.signature || null,
          signatureMaxWidth: selectedTeamMember.signatureMaxWidth || 150
        } : null,
        updatedAt: Date.now()
      };

      // Wenn wir bereits eine offerId haben (Edit-Mode oder bereits auto-saved)
      let currentOfferId = offerId;
      
      if (offerId) {
        const offerRef = ref(database, `/World/${userId}/offers/${offerId}`);
        await set(offerRef, updatedOffer);
      } else {
        // Neues Angebot: Erstelle eine neue ID
        const offersRef = ref(database, `/World/${userId}/offers`);
        const newOfferRef = push(offersRef);
        currentOfferId = newOfferRef.key;
        
        // Füge createdAt hinzu für neue Angebote
        const newOffer = {
          ...updatedOffer,
          createdAt: updatedOffer.createdAt || Date.now()
        };
        
        await set(newOfferRef, newOffer);
        
        // Setze die offerId für zukünftige Auto-Saves
        setOfferId(currentOfferId);
        setAutoSaveId(currentOfferId);
      }
      
      setLastAutoSave(new Date());
      setLastSavedOffer(offerToSave);
      
      // Speichere auch im localStorage als Backup
      const localDraftKey = `offer_draft_${userId}`;
      const draftData = {
        ...updatedOffer,
        savedAt: Date.now(),
        offerId: currentOfferId
      };
      localStorage.setItem(localDraftKey, JSON.stringify(draftData));
      
      console.log("✅ Auto-saved successfully (DB + LocalStorage) with services:", offerToSave.services);
    } catch (error) {
      console.error("Auto-Save Error:", error);
    }
  };

  // Auto-Save Effect - Speichert automatisch alle 5 Sekunden
  useEffect(() => {
    // Auto-Save funktioniert sowohl für neue als auch bestehende Angebote
    // Bedingung: Es muss einen Titel geben
    if (currentOffer.title && currentOffer.title.trim() !== '') {
      const timeoutId = setTimeout(() => {
        autoSaveOffer(currentOffer);
      }, 5000); // Warte 5 Sekunden nach der letzten Änderung

      return () => clearTimeout(timeoutId);
    }
  }, [currentOffer, services, selectedTeamMember, isEditMode, offerId]);

  // Speichere die initiale Angebotsversion für die Änderungserkennung
  useEffect(() => {
    if (initialOfferData && isEditMode) {
      setLastSavedOffer(initialOfferData);
    }
  }, [initialOfferData, isEditMode]);

  // Modifiziere handleToggleService um den autoSaveId zu berücksichtigen
  const handleToggleService = (serviceId) => {
    setCurrentOffer(prevOffer => {
      const updatedOffer = {
        ...prevOffer,
        services: prevOffer.services.includes(serviceId)
          ? prevOffer.services.filter(id => id !== serviceId)
          : [...prevOffer.services, serviceId]
      };

      // Wenn es der erste Service ist, setze einen Titel falls keiner existiert
      if (updatedOffer.services.length === 1 && !updatedOffer.title) {
        const service = services.find(s => s.id === serviceId);
        if (service) {
          updatedOffer.title = `Angebot: ${service.title}`;
        }
      }

      return updatedOffer;
    });
  };

  // Upload logo
  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check if it's an image
    if (!file.type.match('image.*')) {
      showNotification("Please select an image file (jpg, png, etc.)", "error");
      return;
    }
    
    // Check size limit (max. 2MB)
    if (file.size > 2 * 1024 * 1024) {
      showNotification("The image file is too large (max. a2MB)", "error");
      return;
    }
    
    setLogoFile(file);
    
    // Display preview image
    const reader = new FileReader();
    reader.onload = (event) => {
      setLogoUrl(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Remove logo
  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoUrl("");
    setCurrentOffer(prevOffer => ({
      ...prevOffer,
      logo: ""
    }));
  };

  // Handle team member selection
  const handleTeamMemberChange = (e) => {
    const memberId = e.target.value;
    const member = teamMembers.find(m => m.id === memberId);
    setSelectedTeamMember(member || null);
  };

  // Draft Recovery Handlers
  const handleRestoreDraft = () => {
    if (draftData) {
      setCurrentOffer({
        title: draftData.title || "",
        description: draftData.description || "",
        services: draftData.services || [],
        microserviceOverrides: draftData.microserviceOverrides || {},
        additionalMicroservices: draftData.additionalMicroservices || {},
        serviceOverrides: draftData.serviceOverrides || {},
        notes: draftData.notes || "",
        terms: draftData.terms || "",
        showSignatureSection: draftData.showSignatureSection || false,
        signatureCustomerName: draftData.signatureCustomerName || "",
        signatureCustomerRole: draftData.signatureCustomerRole || "",
        signaturePlace: draftData.signaturePlace || "",
        logo: draftData.logo || "",
        logoMaxHeight: draftData.logoMaxHeight || 80,
        createdAt: draftData.createdAt || Date.now(),
        offerValidTill: draftData.offerValidTill || "",
        estimatedTimespan: draftData.estimatedTimespan || "",
        isRecurring: draftData.isRecurring || false,
        customerName: draftData.customerName || "",
        customerAddress: draftData.customerAddress || "",
        globalDiscount: draftData.globalDiscount || 0,
        showPaymentButton: draftData.showPaymentButton || false,
        paymentButtonText: draftData.paymentButtonText || "Pay with Stripe",
        paymentButtonLink: draftData.paymentButtonLink || "",
        showSenderName: draftData.showSenderName !== undefined ? draftData.showSenderName : true,
        teamMember: draftData.teamMember || null,
        language: draftData.language || "en",
        includeTax: draftData.includeTax || false,
        taxRate: draftData.taxRate || 20,
        reverseCharge: draftData.reverseCharge || false,
        documentType: draftData.documentType || "offer",
        invoiceNumber: draftData.invoiceNumber || "",
        invoiceDate: draftData.invoiceDate || "",
        showOfferNumber: draftData.showOfferNumber || false,
        offerNumber: draftData.offerNumber || ""
      });
      
      if (draftData.offerId) {
        setOfferId(draftData.offerId);
      }
      
      if (draftData.logo) {
        setLogoUrl(draftData.logo);
      }
      
      showNotification("Draft restored successfully", "success");
    }
    setShowDraftModal(false);
  };

  const handleDiscardDraft = () => {
    const localDraftKey = `offer_draft_${userId}`;
    localStorage.removeItem(localDraftKey);
    setDraftData(null);
    setHasDraftRecovery(false);
    setShowDraftModal(false);
    showNotification("Draft discarded", "info");
  };

  // Sync offer to public offers collection with all necessary data
  const syncToPublicOffers = async (offerIdToSync, offerData) => {
    try {
      const customId = `${userId}+${offerIdToSync}`;
      
      // Get services data
      let servicesData = [];
      if (offerData.services && offerData.services.length > 0) {
        const servicesRef = ref(database, `/World/${userId}/services`);
        const servicesSnapshot = await get(servicesRef);
        
        if (servicesSnapshot.exists()) {
          const allServices = servicesSnapshot.val();
          servicesData = offerData.services
            .map(serviceId => {
              const service = allServices[serviceId];
              return service ? { id: serviceId, ...service } : null;
            })
            .filter(service => service !== null);
        }
      }
      
      // Get team member data if available
      let teamMemberData = null;
      if (offerData.teamMemberId) {
        const teamMemberRef = ref(database, `/World/${userId}/team/members/${offerData.teamMemberId}`);
        const teamMemberSnapshot = await get(teamMemberRef);
        if (teamMemberSnapshot.exists()) {
          teamMemberData = teamMemberSnapshot.val();
        }
      }
      
      // Calculate pricing with updated services
      const pricing = calculateOfferPricing(offerData, servicesData);
      
      // Create public offer with all data including calculated pricing
      const publicOfferData = {
        ...offerData,
        services: servicesData,
        teamMember: teamMemberData,
        userId,
        offerId: offerIdToSync,
        lastUpdated: Date.now(),
        // Include calculated pricing
        ...pricing
      };
      
      const publicOfferRef = ref(database, `/publicOffers/${customId}`);
      await set(publicOfferRef, publicOfferData);
      
      console.log(`✅ Synced offer ${offerIdToSync} to public offers`);
    } catch (error) {
      console.error("Error syncing to public offers:", error);
      // Don't throw error, just log it - sync failure shouldn't break saving
    }
  };

  // ---------- Sharing from inside the editor ----------
  // The public link used to be reachable only from the dashboard, which meant
  // leaving the document to publish it. These helpers write the visibility
  // flag on its own so the toggle takes effect without a full save.
  const publicLink = offerId
    ? `${window.location.origin}/custom-offer/${userId}+${offerId}`
    : "";

  const applyPublicState = async (nextIsPublic) => {
    if (!offerId) return false;
    try {
      await update(ref(database, `/World/${userId}/offers/${offerId}`), {
        isPublic: nextIsPublic,
      });

      if (nextIsPublic) {
        await syncToPublicOffers(offerId, {
          ...currentOffer,
          teamMemberId: selectedTeamMember?.id || null,
          isPublic: true,
        });
      } else {
        await remove(ref(database, `/publicOffers/${userId}+${offerId}`));
      }

      setCurrentOffer((prev) => ({ ...prev, isPublic: nextIsPublic }));
      return true;
    } catch (error) {
      console.error("Error updating offer visibility:", error);
      showNotification("Error updating visibility", "error");
      return false;
    }
  };

  const handleTogglePublic = async () => {
    const nextIsPublic = !currentOffer.isPublic;
    const ok = await applyPublicState(nextIsPublic);
    if (ok) {
      showNotification(
        nextIsPublic ? "Offer is now public" : "Offer is private again",
        "success"
      );
    }
  };

  const handleCopyPublicLink = async () => {
    if (!offerId) return;
    if (!currentOffer.isPublic && !(await applyPublicState(true))) return;
    try {
      await navigator.clipboard.writeText(publicLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
      showNotification("Link copied to clipboard", "success");
    } catch (error) {
      console.error("Error copying link:", error);
      showNotification("Error copying link", "error");
    }
  };

  const handleOpenPublicLink = async () => {
    if (!offerId) return;
    if (!currentOffer.isPublic) await applyPublicState(true);
    window.open(publicLink, "_blank");
  };

  // Invoice numbers must be unique, so the check is repeated against the live
  // database instead of trusting the snapshot behind the inline hint.
  const findInvoiceNumberConflict = async (value) => {
    const snapshot = await get(ref(database, `/World/${userId}/offers`));
    const index = buildNumberIndex(
      collectDocumentNumbers(snapshot.exists() ? snapshot.val() : {}, "invoice")
    );
    const result = analyzeDocumentNumber(value, index, {
      currentId: offerId,
      noun: "invoice number",
    });
    return result.status === "duplicate" ? result : null;
  };

  const handleSaveOffer = async () => {
    if (!userId) return;
    
    if (!currentOffer.services || currentOffer.services.length === 0) {
      showNotification("Please add at least one service", "error");
      return;
    }

    if (!currentOffer.title) {
      showNotification("Please enter a title", "error");
      return;
    }

    // Validate invoice number if document type is invoice
    if (currentOffer.documentType === 'invoice') {
      if (!currentOffer.invoiceNumber) {
        showNotification("Please enter an invoice number", "error");
        return;
      }

      const conflict = await findInvoiceNumberConflict(currentOffer.invoiceNumber);
      if (conflict) {
        showNotification(
          conflict.suggestion
            ? `${conflict.message} Next free number: ${conflict.suggestion}`
            : conflict.message,
          "error"
        );
        return;
      }
    }

    // Upload logo, if available
    let finalLogoUrl = currentOffer.logo;
    if (logoFile) {
      setIsUploading(true);
      
      try {
        const timestamp = new Date().getTime();
        const fileExtension = logoFile.name.split('.').pop();
        const fileName = `offer_logos/${timestamp}_${fileExtension}`;
        const fileRef = storageRef(storage, fileName);
        
        await uploadBytes(fileRef, logoFile);
        finalLogoUrl = await getDownloadURL(fileRef);
        
        setIsUploading(false);
      } catch (error) {
        console.error("Error uploading logo:", error);
        showNotification("The logo could not be uploaded. Please try again.", "error");
        setIsUploading(false);
        return;
      }
    }

    // Calculate price information - using serviceOverrides if available
    const { originalTotal, servicesDiscountedTotal, discountedTotal, taxAmount, finalTotal } =
      calculateOfferPricing(
        currentOffer,
        services.filter(service => currentOffer.services.includes(service.id))
      );

    // Complete offer object
    const updatedOffer = {
      ...currentOffer,
      logo: finalLogoUrl,
      originalTotal,
      servicesDiscountedTotal,
      discountedTotal,
      taxAmount,
      finalTotal,
      teamMemberId: selectedTeamMember?.id || null,
      teamMember: selectedTeamMember ? {
        name: selectedTeamMember.name,
        role: selectedTeamMember.role || "",
        companyName: selectedTeamMember.companyName,
        companySlogan: selectedTeamMember.companySlogan,
        personalEmail: selectedTeamMember.personalEmail,
        generalEmail: selectedTeamMember.generalEmail,
        phone: selectedTeamMember.phone,
        website: selectedTeamMember.website,
        bankName: selectedTeamMember.bankName,
        iban: selectedTeamMember.iban,
        bic: selectedTeamMember.bic,
        signature: selectedTeamMember.signature || null,
        signatureMaxWidth: selectedTeamMember.signatureMaxWidth || 150
      } : null,
      updatedAt: Date.now()
    };

    try {
      // Determine the reference path based on the mode (Edit/New)
      let offerRef;
      if (offerId) {
        // When we already have a record (edit mode, or after a previous
        // save in this session), update it instead of creating a duplicate
        offerRef = ref(database, `/World/${userId}/offers/${offerId}`);
        
        // Remove ID from the object before saving
        const { id, ...offerWithoutId } = updatedOffer;
        
        await set(offerRef, offerWithoutId);
        
        // If offer is public, sync to publicOffers
        if (offerWithoutId.isPublic) {
          await syncToPublicOffers(offerId, offerWithoutId);
        }
        
        showNotification("Offer successfully updated", "success");
      } else {
        // When creating, create a new record
        offerRef = ref(database, `/World/${userId}/offers`);
        const newOfferRef = push(offerRef);
        
        // For a new offer, set the creation date
        const newOffer = {
          ...updatedOffer,
          createdAt: updatedOffer.createdAt || Date.now(),
        };
        
        await set(newOfferRef, newOffer);
        
        // If new offer is public, sync to publicOffers
        if (newOffer.isPublic) {
          await syncToPublicOffers(newOfferRef.key, newOffer);
        }

        // Keep editing the freshly created offer (so subsequent saves
        // update the same record instead of creating duplicates)
        setOfferId(newOfferRef.key);
        setCurrentOffer((prev) => ({ ...prev, createdAt: newOffer.createdAt }));

        showNotification("Offer successfully saved", "success");
      }

      // Clear the local draft – there are no unsaved changes anymore
      try {
        localStorage.removeItem(`offer_draft_${userId}`);
      } catch (e) {
        // ignore storage errors
      }
    } catch (error) {
      console.error("Error saving offer:", error);
      showNotification("Error saving offer", "error");
    }

    setAutoSaveId(null); // Reset autoSaveId nach manuellem Speichern
  };

  // Show and hide notification
  const showNotification = (message, type) => {
    setNotification({ message, type });
    
    // Automatically hide after 3 seconds
    setTimeout(() => {
      setNotification({ message: "", type: "" });
    }, 3000);
  };

  // Filter services
  const getFilteredServices = () => {
    return services.filter(service => {
      const matchesSearch = searchTerm.trim() === "" || 
        service.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = categoryFilter === "" || service.category === categoryFilter;
      
      return matchesSearch && matchesCategory;
    });
  };

  // Available services (for modal)
  const availableServices = getFilteredServices();

  // Services in the offer
  const offerServices = services.filter(
    service => currentOffer.services.includes(service.id)
  );

  // Open modal
  const openServiceModal = () => {
    setSearchTerm("");
    setCategoryFilter("");
    setIsModalOpen(true);
  };

  // Open microservice editor for a specific service
  const openMicroserviceEditor = (service) => {
    setEditingServiceForMicroservices(service);
    setIsMicroserviceEditorOpen(true);
  };

  // Per-offer deviations of a catalog service (price, label, billing mode).
  // Passing undefined for a key drops that deviation again.
  const updateServiceOverride = (serviceId, patch) => {
    setCurrentOffer(prev => {
      const allOverrides = { ...(prev.serviceOverrides || {}) };
      const next = { ...(allOverrides[serviceId] || {}) };

      Object.entries(patch).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          delete next[key];
        } else {
          next[key] = value;
        }
      });

      if (Object.keys(next).length === 0) {
        delete allOverrides[serviceId];
      } else {
        allOverrides[serviceId] = next;
      }

      return { ...prev, serviceOverrides: allOverrides };
    });
  };

  const resetServiceOverride = (serviceId) => {
    setCurrentOffer(prev => {
      if (!prev.serviceOverrides?.[serviceId]) return prev;
      const allOverrides = { ...prev.serviceOverrides };
      delete allOverrides[serviceId];
      return { ...prev, serviceOverrides: allOverrides };
    });
  };

  // Save microservice overrides for a specific service
  const handleSaveMicroserviceOverrides = (serviceId, overrides, additionalMicroservices = [], serviceOverride = {}, removedMicroservices = []) => {
    setCurrentOffer(prev => {
      const newOverrides = { ...prev.microserviceOverrides };
      const newAdditional = { ...(prev.additionalMicroservices || {}) };
      const newServiceOverrides = { ...(prev.serviceOverrides || {}) };
      const newRemoved = { ...(prev.removedMicroservices || {}) };
      
      // Handle microservice overrides
      if (Object.keys(overrides).length === 0) {
        delete newOverrides[serviceId];
      } else {
        newOverrides[serviceId] = overrides;
      }
      
      // Handle additional microservices
      if (additionalMicroservices.length === 0) {
        delete newAdditional[serviceId];
      } else {
        newAdditional[serviceId] = additionalMicroservices;
      }
      
      // Handle service-level overrides (title, price)
      if (Object.keys(serviceOverride).length === 0) {
        delete newServiceOverrides[serviceId];
      } else {
        newServiceOverrides[serviceId] = serviceOverride;
      }
      
      // Handle removed microservices
      if (removedMicroservices.length === 0) {
        delete newRemoved[serviceId];
      } else {
        newRemoved[serviceId] = removedMicroservices;
      }
      
      return {
        ...prev,
        microserviceOverrides: newOverrides,
        additionalMicroservices: newAdditional,
        removedMicroservices: newRemoved,
        serviceOverrides: newServiceOverrides
      };
    });
    
    showNotification("Changes saved for this offer", "success");
  };

  // Update offer information
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Once the payment due date is edited by hand it stays put, even if the
    // creation date changes again afterwards.
    if (name === 'offerValidTill') {
      autoPaymentDueRef.current = null;
    }
    setCurrentOffer(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Changing the creation date carries the invoice payment due date along
  // (creation + 7 days) as long as that field was never touched manually.
  const handleCreatedAtChange = (dateValue) => {
    const previous = currentOffer.createdAt ? new Date(currentOffer.createdAt) : null;
    let created;
    if (dateValue) {
      const [year, month, day] = dateValue.split('-').map(Number);
      created = new Date(
        year,
        (month || 1) - 1,
        day || 1,
        // Preserve original time-of-day on existing timestamp; default to midday
        previous ? previous.getHours() : 12,
        previous ? previous.getMinutes() : 0,
        previous ? previous.getSeconds() : 0
      );
    } else {
      created = new Date();
    }

    const due = toDateInputValue(
      new Date(created.getFullYear(), created.getMonth(), created.getDate() + PAYMENT_TERM_DAYS)
    );
    const dueIsUntouched =
      !currentOffer.offerValidTill ||
      currentOffer.offerValidTill === autoPaymentDueRef.current;
    const syncDue = currentOffer.documentType === 'invoice' && dueIsUntouched;
    if (syncDue) autoPaymentDueRef.current = due;

    setCurrentOffer(prev => ({
      ...prev,
      createdAt: created.getTime(),
      ...(syncDue ? { offerValidTill: due } : {}),
    }));
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link'
  ];

  const isInvoice = currentOffer.documentType === 'invoice';

  // Stable snapshot for the reissue dialog: the sender id only exists on the
  // document after a save, but the follow-up invoice needs it right away.
  const reissueSource = useMemo(
    () => ({
      ...currentOffer,
      teamMemberId: selectedTeamMember?.id || currentOffer.teamMemberId || null,
    }),
    [currentOffer, selectedTeamMember]
  );
  const savedAtLabel = lastAutoSave
    ? lastAutoSave.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : null;

  const shareMenuItems = [
    {
      id: 'visibility',
      icon: currentOffer.isPublic ? 'fa-lock' : 'fa-globe',
      label: currentOffer.isPublic ? 'Make private' : 'Make public',
      description: currentOffer.isPublic
        ? 'Takes the public page offline — the existing link stops working'
        : 'Creates a public page that anyone with the link can open',
      onSelect: handleTogglePublic,
    },
    {
      id: 'copy',
      icon: linkCopied ? 'fa-check' : 'fa-link',
      label: linkCopied ? 'Link copied' : 'Copy link',
      description: 'Publishes the document if needed, then copies its link',
      onSelect: handleCopyPublicLink,
    },
    {
      id: 'open',
      icon: 'fa-arrow-up-right-from-square',
      label: 'Open public page',
      description: 'Opens the customer view in a new tab',
      onSelect: handleOpenPublicLink,
    },
  ];

  return (
    <div className="offer-maker">
      {notification.message && (
        <div className={`offer-maker__notification offer-maker__notification--${notification.type}`}>
          {notification.message}
        </div>
      )}
      
      <div className="offer-maker__topbar">
        <button type="button" className="offer-maker__back" onClick={onBack}>
          <i className="fas fa-chevron-left" aria-hidden="true"></i>
          Offers
        </button>

        <div className="offer-maker__topbar-doc">
          <span className="offer-maker__topbar-title">
            {currentOffer.title || (isInvoice ? "Untitled invoice" : "Untitled offer")}
          </span>
          <span className="offer-maker__topbar-badge">
            {isInvoice ? "Invoice" : "Offer"}
          </span>
          {currentOffer.isPublic && (
            <span className="offer-maker__topbar-badge offer-maker__topbar-badge--live">
              <i className="fas fa-globe" aria-hidden="true"></i>
              Public
            </span>
          )}
        </div>

        <div className="offer-maker__topbar-actions">
          <span
            className={`offer-maker__save-state ${isUploading ? "is-busy" : ""}`}
            title="This document saves itself a few seconds after every change"
          >
            {isUploading
              ? "Saving…"
              : savedAtLabel
                ? `Saved ${savedAtLabel}`
                : "Not saved yet"}
          </span>

          <button
            type="button"
            className="offer-maker__topbar-btn"
            onClick={() => setIsReissueOpen(true)}
            disabled={!offerId || currentOffer.services.length === 0}
            title={
              offerId
                ? "Bill the same positions again with today's date"
                : "Save the document once before reissuing it"
            }
          >
            <i className="fas fa-file-invoice-dollar" aria-hidden="true"></i>
            {isInvoice ? "Reissue" : "To invoice"}
          </button>

          <DropdownMenu
            items={shareMenuItems}
            align="end"
            width={318}
            disabled={!offerId}
            ariaLabel="Share this document"
            triggerTitle={
              offerId
                ? "Share this document"
                : "Save the document once to get a link"
            }
            triggerClassName="offer-maker__topbar-btn"
            triggerContent={
              <>
                <i className="fas fa-share-nodes" aria-hidden="true"></i>
                Share
              </>
            }
          />

          <button
            className="offer-maker__save-btn"
            onClick={handleSaveOffer}
            disabled={isUploading || currentOffer.services.length === 0 || !currentOffer.title}
          >
            {isUploading ? "Saving…" : (isEditMode || offerId) ? "Save changes" : "Save offer"}
          </button>
        </div>
      </div>
      
      <div className="offer-maker__content">
        {/* Left column: Editor */}
        <div className="offer-maker__editor">
          <div className="offer-maker__section">
            <div className="offer-maker__section-header">
              <span className="offer-maker__section-icon">
                {currentOffer.documentType === 'invoice' ? '🧾' : '📝'}
              </span>
              <h3 className="offer-maker__section-title">
                {currentOffer.documentType === 'invoice' ? 'Invoice Information' : 'Offer Information'}
              </h3>
            </div>

            <div className="offer-maker__language-toggle">
              <label className="offer-maker__language-label">Document Type</label>
              <div className="offer-maker__toggle-container">
                <label className="offer-maker__toggle-switch">
                  <input
                    type="checkbox"
                    className="offer-maker__toggle-input"
                    checked={currentOffer.documentType === 'invoice'}
                    onChange={(e) => setCurrentOffer(prev => ({
                      ...prev,
                      documentType: e.target.checked ? 'invoice' : 'offer'
                    }))}
                  />
                  <span className="offer-maker__toggle-slider"></span>
                </label>
                <span className="offer-maker__toggle-label">
                  {currentOffer.documentType === 'invoice' ? '🧾 Invoice' : '📄 Offer'}
                </span>
              </div>
            </div>

            <div className="offer-maker__language-toggle">
              <label className="offer-maker__language-label">Language</label>
              <div className="offer-maker__toggle-container">
                <label className="offer-maker__toggle-switch">
                  <input
                    type="checkbox"
                    className="offer-maker__toggle-input"
                    checked={currentOffer.language === 'de'}
                    onChange={(e) => setCurrentOffer(prev => ({
                      ...prev,
                      language: e.target.checked ? 'de' : 'en'
                    }))}
                  />
                  <span className="offer-maker__toggle-slider"></span>
                </label>
                <span className="offer-maker__toggle-label">
                  {currentOffer.language === 'de' ? '🇩🇪 Deutsch' : '🇺🇸 English'}
                </span>
              </div>
            </div>
            
            <div className="offer-maker__form-group">
              <label className="offer-maker__label">
                {currentOffer.documentType === 'invoice' ? 'Invoice Title' : 'Offer Title'}
              </label>
              <textarea
                className="offer-maker__input offer-maker__input--title"
                value={currentOffer.title || ''}
                onChange={(e) => setCurrentOffer(prev => ({ ...prev, title: e.target.value }))}
                placeholder={currentOffer.documentType === 'invoice' ? 'Enter invoice title' : 'Enter offer title'}
                rows="2"
              />
            </div>

            {currentOffer.documentType === 'invoice' && (
              <>
                <DocumentNumberField
                  label="Invoice Number"
                  required
                  value={currentOffer.invoiceNumber}
                  placeholder="e.g. INV-2026-001"
                  analysis={invoiceNumberAnalysis}
                  onChange={(value) =>
                    setCurrentOffer(prev => ({ ...prev, invoiceNumber: value }))
                  }
                  onApplySuggestion={(value) =>
                    setCurrentOffer(prev => ({ ...prev, invoiceNumber: value }))
                  }
                />

                <div className="offer-maker__form-group">
                  <label className="offer-maker__label">Invoice Date</label>
                  <input
                    type="date"
                    className="offer-maker__input"
                    value={currentOffer.invoiceDate || ''}
                    onChange={(e) => setCurrentOffer(prev => ({ ...prev, invoiceDate: e.target.value }))}
                  />
                </div>
              </>
            )}

            {currentOffer.documentType !== 'invoice' && (
              <>
                <div className="offer-maker__billing-toggle">
                  <label className="offer-maker__toggle-switch">
                    <input
                      type="checkbox"
                      className="offer-maker__toggle-input"
                      checked={!!currentOffer.showOfferNumber}
                      onChange={(e) => setCurrentOffer(prev => ({
                        ...prev,
                        showOfferNumber: e.target.checked
                      }))}
                    />
                    <span className="offer-maker__toggle-slider"></span>
                  </label>
                  <span className="offer-maker__toggle-label">
                    Show offer number
                  </span>
                </div>

                {currentOffer.showOfferNumber && (
                  <DocumentNumberField
                    label="Offer Number"
                    value={currentOffer.offerNumber}
                    placeholder="e.g. OFF-2026-001"
                    analysis={offerNumberAnalysis}
                    onChange={(value) =>
                      setCurrentOffer(prev => ({ ...prev, offerNumber: value }))
                    }
                    onApplySuggestion={(value) =>
                      setCurrentOffer(prev => ({ ...prev, offerNumber: value }))
                    }
                  />
                )}
              </>
            )}
            
            <div className="offer-maker__input-group">
              <label className="offer-maker__label">Customer Name</label>
              <input
                type="text"
                name="customerName"
                value={currentOffer.customerName}
                onChange={handleInputChange}
                className="offer-maker__input"
                placeholder="Customer name"
              />
            </div>
            
            <div className="offer-maker__input-group">
              <label className="offer-maker__label">Customer Address</label>
              <textarea
                name="customerAddress"
                value={currentOffer.customerAddress}
                onChange={handleInputChange}
                className="offer-maker__input"
                placeholder="Street, Number&#10;ZIP Code, City&#10;Country"
                rows="3"
                style={{ resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>
            
            <div className="offer-maker__input-group">
              <label className="offer-maker__label">Description</label>
              <ReactQuill
                value={currentOffer.description}
                onChange={(value) => setCurrentOffer(prev => ({ ...prev, description: value }))}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Describe your offer..."
                className="offer-maker__quill-editor"
              />
            </div>
            
            <div className="offer-maker__input-group">
              <label className="offer-maker__label">
                {currentOffer.language === 'de' ? 'Erstellt am' : 'Created on'}
              </label>
              <input
                type="date"
                name="createdAt"
                value={currentOffer.createdAt ? toDateInputValue(currentOffer.createdAt) : ''}
                onChange={(e) => handleCreatedAtChange(e.target.value)}
                className="offer-maker__input"
              />
            </div>

            <div className="offer-maker__input-group">
              <label className="offer-maker__label">
                {currentOffer.documentType === 'invoice' 
                  ? (currentOffer.language === 'de' ? 'Zahlungsziel' : 'Payment Due')
                  : (currentOffer.language === 'de' ? 'Gültig bis' : 'Valid until')
                }
              </label>
              <input
                type="date"
                name="offerValidTill"
                value={currentOffer.offerValidTill}
                onChange={handleInputChange}
                className="offer-maker__input"
                placeholder="MM/DD/YYYY"
              />
            </div>
            
            <div className="offer-maker__input-group">
              <label className="offer-maker__label">Estimated delivery time</label>
              <input
                type="text"
                name="estimatedTimespan"
                value={currentOffer.estimatedTimespan}
                onChange={handleInputChange}
                className="offer-maker__input"
                placeholder="e.g. 2-3 weeks or 10 business days"
              />
            </div>
            
            <div className="offer-maker__input-group">
              <label className="offer-maker__label">Global Discount (%)</label>
              <input
                type="number"
                name="globalDiscount"
                value={currentOffer.globalDiscount}
                onChange={handleInputChange}
                className="offer-maker__input"
                placeholder="e.g. 20 for 20% discount"
                min="0"
                max="100"
              />
            </div>

            <div className="offer-maker__billing-toggle">
              <label className="offer-maker__toggle-switch">
                <input
                  type="checkbox"
                  className="offer-maker__toggle-input"
                  checked={currentOffer.includeTax}
                  onChange={(e) => setCurrentOffer(prev => ({
                    ...prev,
                    includeTax: e.target.checked
                  }))}
                />
                <span className="offer-maker__toggle-slider"></span>
              </label>
              <span className="offer-maker__toggle-label">
                Include taxes in price
              </span>
            </div>

            {currentOffer.includeTax && (
              <div className="offer-maker__input-group">
                <label className="offer-maker__label">Tax Rate (%)</label>
                <input
                  type="number"
                  name="taxRate"
                  value={currentOffer.taxRate}
                  onChange={handleInputChange}
                  className="offer-maker__input"
                  placeholder="e.g. 19 for 19% VAT"
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>
            )}

            <div className="offer-maker__billing-toggle">
              <label className="offer-maker__toggle-switch">
                <input
                  type="checkbox"
                  className="offer-maker__toggle-input"
                  checked={currentOffer.reverseCharge}
                  onChange={(e) => setCurrentOffer(prev => ({
                    ...prev,
                    reverseCharge: e.target.checked
                  }))}
                />
                <span className="offer-maker__toggle-slider"></span>
              </label>
              <span className="offer-maker__toggle-label">
                Reverse Charge
              </span>
            </div>



            <div className="offer-maker__section-divider"></div>

            <div className="offer-maker__billing-toggle">
              <label className="offer-maker__toggle-switch">
                <input
                  type="checkbox"
                  className="offer-maker__toggle-input"
                  checked={currentOffer.showPaymentButton}
                  onChange={(e) => setCurrentOffer(prev => ({
                    ...prev,
                    showPaymentButton: e.target.checked
                  }))}
                />
                <span className="offer-maker__toggle-slider"></span>
              </label>
              <span className="offer-maker__toggle-label">
                Show payment button
              </span>
            </div>

            {currentOffer.showPaymentButton && (
              <>
                <div className="offer-maker__input-group">
                  <label className="offer-maker__label">Button Text</label>
                  <input
                    type="text"
                    name="paymentButtonText"
                    value={currentOffer.paymentButtonText}
                    onChange={handleInputChange}
                    className="offer-maker__input"
                    placeholder="e.g. Pay with Stripe"
                  />
                </div>

                <div className="offer-maker__input-group">
                  <label className="offer-maker__label">Payment Link</label>
                  <input
                    type="url"
                    name="paymentButtonLink"
                    value={currentOffer.paymentButtonLink}
                    onChange={handleInputChange}
                    className="offer-maker__input"
                    placeholder="https://stripe.com/pay/..."
                  />
                  <small className="offer-maker__input-hint">
                    Der Link wird in einem neuen Tab geöffnet
                  </small>
                </div>
              </>
            )}

            <div className="offer-maker__section-divider"></div>

            <div className="offer-maker__input-group">
              <label className="offer-maker__label">Additional Notes</label>
              <ReactQuill
                value={currentOffer.notes}
                onChange={(value) => setCurrentOffer(prev => ({ ...prev, notes: value }))}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Additional information such as payment terms, delivery time, etc."
                className="offer-maker__quill-editor"
              />
            </div>

            <div className="offer-maker__input-group">
              <label className="offer-maker__label">
                {currentOffer.language === 'de' ? 'Angebotsbedingungen' : 'Terms & Conditions'}
              </label>
              <ReactQuill
                value={currentOffer.terms}
                onChange={(value) => setCurrentOffer(prev => ({ ...prev, terms: value }))}
                modules={quillModules}
                formats={quillFormats}
                placeholder={currentOffer.language === 'de'
                  ? 'Gewährleistung, Haftung, Eigentumsvorbehalt, Stornierungsbedingungen, Gerichtsstand, …'
                  : 'Warranty, liability, retention of title, cancellation policy, jurisdiction, …'
                }
                className="offer-maker__quill-editor"
              />
              <small className="offer-maker__input-hint">
                {currentOffer.language === 'de'
                  ? 'Wird unten im Angebot in einem eigenen Block angezeigt.'
                  : 'Displayed in its own section at the bottom of the offer.'
                }
              </small>
            </div>

            <div className="offer-maker__billing-toggle">
              <label className="offer-maker__toggle-switch">
                <input
                  type="checkbox"
                  className="offer-maker__toggle-input"
                  checked={currentOffer.showSignatureSection}
                  onChange={(e) => setCurrentOffer(prev => ({
                    ...prev,
                    showSignatureSection: e.target.checked
                  }))}
                />
                <span className="offer-maker__toggle-slider"></span>
              </label>
              <span className="offer-maker__toggle-label">
                {currentOffer.language === 'de'
                  ? 'Unterschriftsfeld am Ende des Angebots anzeigen'
                  : 'Show signature section at the end of the offer'
                }
              </span>
            </div>

            {currentOffer.showSignatureSection && (
              <div className="offer-maker__signature-settings">
                <div className="offer-maker__input-group">
                  <label className="offer-maker__label">
                    {currentOffer.language === 'de' ? 'Ort (Ort, Datum)' : 'Place (Place, Date)'}
                  </label>
                  <input
                    type="text"
                    name="signaturePlace"
                    value={currentOffer.signaturePlace}
                    onChange={handleInputChange}
                    className="offer-maker__input"
                    placeholder={currentOffer.language === 'de' ? 'z. B. München' : 'e.g. Munich'}
                  />
                  <small className="offer-maker__input-hint">
                    {currentOffer.language === 'de'
                      ? 'Optional. Wird über beiden Unterschriftslinien angezeigt. Datum wird automatisch eingefügt.'
                      : 'Optional. Shown above both signature lines. Date is filled automatically.'
                    }
                  </small>
                </div>

                <div className="offer-maker__input-group">
                  <label className="offer-maker__label">
                    {currentOffer.language === 'de'
                      ? 'Name Unterzeichner Auftraggeber (optional)'
                      : 'Customer signer name (optional)'
                    }
                  </label>
                  <input
                    type="text"
                    name="signatureCustomerName"
                    value={currentOffer.signatureCustomerName}
                    onChange={handleInputChange}
                    className="offer-maker__input"
                    placeholder={currentOffer.customerName
                      ? `${currentOffer.language === 'de' ? 'Standard:' : 'Default:'} ${currentOffer.customerName}`
                      : (currentOffer.language === 'de' ? 'Vor- und Nachname' : 'Full name')
                    }
                  />
                  <small className="offer-maker__input-hint">
                    {currentOffer.language === 'de'
                      ? 'Wenn leer, wird der Kundenname aus den Empfänger-Daten verwendet.'
                      : 'If empty, the customer name from the recipient details is used.'
                    }
                  </small>
                </div>

                <div className="offer-maker__input-group">
                  <label className="offer-maker__label">
                    {currentOffer.language === 'de'
                      ? 'Funktion Unterzeichner Auftraggeber (optional)'
                      : 'Customer signer role (optional)'
                    }
                  </label>
                  <input
                    type="text"
                    name="signatureCustomerRole"
                    value={currentOffer.signatureCustomerRole}
                    onChange={handleInputChange}
                    className="offer-maker__input"
                    placeholder={currentOffer.language === 'de'
                      ? 'z. B. Geschäftsführer, Inhaber'
                      : 'e.g. CEO, Owner'
                    }
                  />
                </div>

                {selectedTeamMember && (
                  <div className="offer-maker__signature-info-box">
                    <div className="offer-maker__signature-info-icon">✍️</div>
                    <div className="offer-maker__signature-info-text">
                      <strong>
                        {currentOffer.language === 'de' ? 'Eure Seite:' : 'Your side:'}
                      </strong>{' '}
                      {selectedTeamMember.name}
                      {selectedTeamMember.role && <> ({selectedTeamMember.role})</>}
                      {' – '}
                      {selectedTeamMember.companyName}
                      {selectedTeamMember.signature && (
                        <span className="offer-maker__signature-info-check">
                          {' '}{currentOffer.language === 'de' ? '✓ Unterschrift hinterlegt' : '✓ Signature on file'}
                        </span>
                      )}
                      {!selectedTeamMember.signature && (
                        <span className="offer-maker__signature-info-warn">
                          {' '}{currentOffer.language === 'de' ? '⚠ Keine Unterschrift hinterlegt' : '⚠ No signature on file'}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="offer-maker__team-select-container">
              <label className="offer-maker__team-select-label">Team Member</label>
              <select
                className="offer-maker__team-select"
                value={selectedTeamMember?.id || ""}
                onChange={handleTeamMemberChange}
              >
                <option value="">Select team member</option>
                {teamMembers.map(member => (
                  <option key={member.id} value={member.id}>
                    {member.name} - {member.companyName}
                  </option>
                ))}
              </select>
              
              {selectedTeamMember && (
                <div className="offer-maker__team-member-card">
                  <div className="offer-maker__team-member-info">
                    <div className="offer-maker__team-member-avatar">
                      {selectedTeamMember.name.charAt(0)}
                    </div>
                    <div className="offer-maker__team-member-details">
                      <div className="offer-maker__team-member-name">
                        {selectedTeamMember.name}
                      </div>
                      <div className="offer-maker__team-member-company">
                        {selectedTeamMember.companyName}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="offer-maker__section-divider"></div>

            <div className="offer-maker__billing-toggle">
              <label className="offer-maker__toggle-switch">
                <input
                  type="checkbox"
                  className="offer-maker__toggle-input"
                  checked={currentOffer.showSenderName}
                  onChange={(e) => setCurrentOffer(prev => ({
                    ...prev,
                    showSenderName: e.target.checked
                  }))}
                />
                <span className="offer-maker__toggle-slider"></span>
              </label>
              <span className="offer-maker__toggle-label">
                Show sender name in "From" section
              </span>
            </div>
          </div>
          
          <div className="offer-maker__section">
            <div className="offer-maker__section-header">
              <span className="offer-maker__section-icon">🛠️</span>
              <h3 className="offer-maker__section-title">Services</h3>
            </div>
            
            <button 
              className="offer-maker__services-btn" 
              onClick={openServiceModal}
            >
              <span className="offer-maker__services-btn-icon">+</span>
              Add services to offer
            </button>
            
            <div className="offer-maker__selected-services">
              {offerServices.length === 0 ? (
                <div className="offer-maker__no-services">
                  <p>No services selected yet</p>
                </div>
              ) : (
                <ul className="offer-maker__services-list">
                  {offerServices.map(service => {
                    const serviceOverride = currentOffer.serviceOverrides?.[service.id] || {};
                    const paymentType = getPaymentType({
                      ...service,
                      paymentType: serviceOverride.paymentType,
                      isRecurring:
                        serviceOverride.paymentType !== undefined
                          ? undefined
                          : (serviceOverride.isRecurring !== undefined
                              ? serviceOverride.isRecurring
                              : service.isRecurring)
                    });
                    const priceSuffix =
                      paymentType === PAYMENT_TYPES.RECURRING ? '/mo'
                      : paymentType === PAYMENT_TYPES.PER_APPOINTMENT
                          ? (currentOffer.language === 'de' ? '/Termin' : '/appt.')
                          : null;

                    // Only microservice level changes, the row detects its own
                    // price/label/billing overrides.
                    const hasCustomizations =
                      Object.keys(currentOffer.microserviceOverrides?.[service.id] || {}).length > 0 ||
                      (currentOffer.additionalMicroservices?.[service.id]?.length > 0) ||
                      (currentOffer.removedMicroservices?.[service.id]?.length > 0);

                    return (
                      <OfferServiceQuickRow
                        key={service.id}
                        service={service}
                        override={serviceOverride}
                        hasCustomizations={hasCustomizations}
                        priceSuffix={priceSuffix}
                        onChangeOverride={updateServiceOverride}
                        onResetOverride={resetServiceOverride}
                        onOpenCustomizer={openMicroserviceEditor}
                        onRemove={handleToggleService}
                      />
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
          
        </div>
        
        {/* Right column - Preview */}
        <div className="offer-maker__preview-container">
          <div className="offer-maker__preview-wrapper">
            {(() => {
              // Calculate prices with service overrides
              const getEffectivePrice = (service) => {
                const override = currentOffer.serviceOverrides?.[service.id];
                return override?.price !== undefined ? override.price : service.price;
              };
              
              const getEffectiveTitle = (service) => {
                const override = currentOffer.serviceOverrides?.[service.id];
                return override?.title || service.title;
              };
              
              const getEffectiveDescription = (service) => {
                const override = currentOffer.serviceOverrides?.[service.id];
                return override?.description || service.description;
              };
              
              const getEffectivePaymentType = (service) => {
                const override = currentOffer.serviceOverrides?.[service.id];
                if (override?.paymentType !== undefined) return override.paymentType;
                if (override?.isRecurring !== undefined) {
                  return override.isRecurring ? PAYMENT_TYPES.RECURRING : PAYMENT_TYPES.ONE_TIME;
                }
                return getPaymentType(service);
              };
              
              // Apply overrides to services for preview (including filtering removed microservices)
              const servicesWithOverrides = offerServices.map(service => {
                // Get removed microservices for this service
                const removedMs = currentOffer.removedMicroservices?.[service.id] || [];
                // Get additional microservices for this service
                const additionalMs = currentOffer.additionalMicroservices?.[service.id] || [];
                const additionalMicroservicesData = microservices.filter(ms => additionalMs.includes(ms.id));
                
                // Filter out removed microservices and add additional ones
                const baseMicroservices = service.microservices || [];
                const filteredMicroservices = baseMicroservices.filter(ms => {
                  const msId = typeof ms === 'object' ? ms.id : ms;
                  return !removedMs.includes(msId);
                });
                
                // Get microservice overrides for this service
                const msOverrides = currentOffer.microserviceOverrides?.[service.id] || {};
                
                // Apply overrides to microservices
                const processedMicroservices = [...filteredMicroservices, ...additionalMicroservicesData].map(ms => {
                  const msData = typeof ms === 'object' ? ms : microservices.find(m => m.id === ms);
                  if (!msData) return null;
                  const override = msOverrides[msData.id] || {};
                  return {
                    ...msData,
                    title: override.title || msData.title,
                    description: override.description || msData.description
                  };
                }).filter(Boolean);
                
                const effectivePaymentType = getEffectivePaymentType(service);
                return {
                  ...service,
                  title: getEffectiveTitle(service),
                  description: getEffectiveDescription(service),
                  price: getEffectivePrice(service),
                  paymentType: effectivePaymentType,
                  isRecurring: isRecurringFromPaymentType(effectivePaymentType),
                  microservices: processedMicroservices
                };
              });
              
              const originalTotal = servicesWithOverrides.reduce((sum, service) => sum + service.price, 0);
              const servicesDiscountedTotal = servicesWithOverrides.reduce((sum, service) => 
                sum + (service.price * (1 - (service.discountPercent || 0) / 100)), 0);
              const discountedTotal = servicesDiscountedTotal * (1 - (currentOffer.globalDiscount / 100));
              const taxAmount = (currentOffer.includeTax && !currentOffer.reverseCharge) ? 
                discountedTotal * (currentOffer.taxRate / 100) : 0;
              const finalTotal = discountedTotal + taxAmount;
              
              return (
                <OfferPreviewPage 
                  offerData={{
                    ...currentOffer,
                    logo: logoUrl || currentOffer.logo,
                    originalTotal,
                    servicesDiscountedTotal,
                    discountedTotal,
                    taxAmount,
                    finalTotal,
                    teamMember: selectedTeamMember,
                    showSenderName: currentOffer.showSenderName
                  }}
                  services={servicesWithOverrides}
                  onlyPreview={true}
                />
              );
            })()}
          </div>
        </div>
      </div>
      
      {/* Modal for service selection */}
      <ServiceSelectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        services={availableServices}
        selectedServiceIds={currentOffer.services}
        onSelectService={handleToggleService}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
        categories={categories}
      />

      {/* Modal for microservice customization */}
      <MicroserviceEditorModal
        isOpen={isMicroserviceEditorOpen}
        onClose={() => {
          setIsMicroserviceEditorOpen(false);
          setEditingServiceForMicroservices(null);
        }}
        service={editingServiceForMicroservices}
        allMicroservices={microservices}
        overrides={editingServiceForMicroservices ? 
          (currentOffer.microserviceOverrides?.[editingServiceForMicroservices.id] || {}) : 
          {}
        }
        serviceOverride={editingServiceForMicroservices ? 
          (currentOffer.serviceOverrides?.[editingServiceForMicroservices.id] || {}) : 
          {}
        }
        additionalMicroservices={editingServiceForMicroservices ? 
          (currentOffer.additionalMicroservices?.[editingServiceForMicroservices.id] || []) : 
          []
        }
        removedMicroservices={editingServiceForMicroservices ? 
          (currentOffer.removedMicroservices?.[editingServiceForMicroservices.id] || []) : 
          []
        }
        onSaveOverrides={handleSaveMicroserviceOverrides}
      />

      {/* Follow-up invoice for the same recipient */}
      {isReissueOpen && (
        <ReissueInvoiceModal
          userId={userId}
          sourceOffer={reissueSource}
          onClose={() => setIsReissueOpen(false)}
          onCreated={({ id, invoice }) => {
            setIsReissueOpen(false);
            showNotification(
              invoice.invoiceNumber
                ? `Invoice ${invoice.invoiceNumber} created`
                : "Invoice created",
              "success"
            );
            // Continue in the new invoice instead of the document it came from.
            navigate(`/deals/${id}`);
          }}
        />
      )}
    </div>
  );
};

export default OfferMaker; 